import { SITE_COMPANY } from '../config/site'

export interface PrivacyPolicyItem {
  label?: string
  text: string
}

export interface PrivacyPolicySection {
  index: string
  title: string
  paragraphs: string[]
  items?: PrivacyPolicyItem[]
  showContactEmail?: boolean
}

export const PRIVACY_POLICY_UPDATED_AT = '3 de novembro de 2026'

export const PRIVACY_POLICY_INTRO: string[] = [
  'Esta política explica quais dados pessoais a Truth Commerce trata quando você visita este site ou fala com a nossa equipe, por que fazemos isso e como você pode exercer os seus direitos.',
  'Ela segue a Lei Geral de Proteção de Dados (Lei nº 13.709/2018) e o Marco Civil da Internet (Lei nº 12.965/2014).',
]

/**
 * Must match what the live site actually does. When a third-party service, cookie or
 * lead-capture field changes, update these sections and PRIVACY_POLICY_UPDATED_AT.
 */
export const PRIVACY_POLICY_SECTIONS: PrivacyPolicySection[] = [
  {
    index: '01',
    title: 'Quem é responsável pelos seus dados',
    paragraphs: [
      `A ${SITE_COMPANY.legalName}, inscrita no CNPJ sob o nº ${SITE_COMPANY.cnpj}, com sede na ${SITE_COMPANY.address}, é a controladora dos dados pessoais tratados neste site.`,
      'Esta política vale para o site truthcommerce.com.br. Serviços contratados pelas empresas clientes podem ter regras próprias, definidas em contrato.',
    ],
  },
  {
    index: '02',
    title: 'Quais dados tratamos',
    paragraphs: [
      'Você não precisa criar conta nem preencher cadastro para navegar no site. Pedimos que não nos envie dados sensíveis, como informações de saúde, pelo chat ou por e-mail.',
    ],
    items: [
      {
        label: 'Conversa no chat',
        text: 'o seu nome e telefone, além de tudo o que você escolher nos enviar durante a conversa.',
      },
      {
        label: 'E-mail',
        text: 'o seu endereço e o conteúdo da mensagem, se você escrever para nós.',
      },
      {
        label: 'Dados técnicos',
        text: 'endereço IP, data e hora de acesso, tipo de navegador e páginas visitadas, que podem chegar à nossa hospedagem e aos serviços listados na seção 04 quando o site carrega.',
      },
    ],
  },
  {
    index: '03',
    title: 'Para que usamos e com qual base legal',
    paragraphs: [
      'Se você registrar interesse no chat, só entramos em contato por meio de chamadas sobre esse interesse, e não enviamos campanhas de marketing.',
      'Usamos os dados para as finalidades abaixo, com as bases legais previstas na LGPD:',
    ],
    items: [
      {
        label: 'Responder ao seu contato e conduzir a conversa comercial que você iniciou',
        text: 'procedimentos preliminares a um contrato, a seu pedido, e legítimo interesse (art. 7º, V e IX).',
      },
      {
        label: 'Manter o site funcionando, seguro e protegido contra abusos',
        text: 'legítimo interesse (art. 7º, IX).',
      },
      {
        label: 'Cumprir obrigações legais e exercer direitos em processos',
        text: 'cumprimento de obrigação legal e exercício regular de direitos (art. 7º, II e VI).',
      },
    ],
  },
  {
    index: '04',
    title: 'Serviços de terceiros que recebem dados',
    paragraphs: [
      'Usamos os fornecedores abaixo, que podem receber dados para prestar os seus serviços:',
    ],
    items: [
      {
        label: 'Pipedrive (LeadBooster)',
        text: 'carrega o chat ao abrir o site e guarda as conversas e os contatos comerciais.',
      },
      {
        label: 'Hostinger',
        text: 'hospeda o site e pode registrar dados técnicos de acesso.',
      },
      {
        label: 'Google Fonts',
        text: 'entrega as fontes do site e recebe o seu IP ao abrir a página.',
      },
      {
        label: 'Unsplash',
        text: 'entrega imagens da seção de serviços e recebe o seu IP quando elas carregam.',
      },
      {
        label: 'YouTube (Google)',
        text: 'só é carregado se você der play no podcast, e o vídeo usa o modo de privacidade aprimorada.',
      },
      {
        label: 'Instagram e LinkedIn',
        text: 'só recebem dados se você clicar nos links para esses sites, onde valem as políticas de cada rede.',
      },
    ],
  },
  {
    index: '05',
    title: 'Cookies e armazenamento no navegador',
    paragraphs: [
      'O site não usa cookies de publicidade nem de análise de audiência. Você pode apagar ou bloquear os itens abaixo nas configurações do navegador, mas o chat pode deixar de funcionar.',
    ],
    items: [
      {
        label: '__cf_bm (Pipedrive/Cloudflare)',
        text: 'cookie de segurança de curta duração, de cerca de 30 minutos, usado para proteger o chat contra tráfego automatizado.',
      },
      {
        label: 'Armazenamento local do chat',
        text: 'quando você abre o chat, o navegador guarda se ele já foi aberto e se continua aberto.',
      },
      {
        label: 'YouTube',
        text: 'se você der play, o player pode usar tecnologias próprias, regidas pela política do Google.',
      },
    ],
  },
  {
    index: '06',
    title: 'Compartilhamento e transferência internacional',
    paragraphs: [
      'Compartilhamos dados com os fornecedores da seção 04 e, quando a lei ou uma ordem judicial exigir, com autoridades.',
      'Alguns desses fornecedores têm operações no exterior, então os dados podem ser tratados fora do Brasil, nos termos da LGPD.',
    ],
  },
  {
    index: '07',
    title: 'Por quanto tempo guardamos',
    paragraphs: [
      'Guardamos os dados de contato e as conversas do chat por 24 meses, contados do nosso último contato com você. Os demais dados ficam guardados pelo tempo necessário para as finalidades desta política, e os registros de acesso ao site são mantidos por pelo menos 6 meses, quando exigido pelo art. 15 do Marco Civil da Internet.',
      'Depois desses prazos, eliminamos ou anonimizamos os dados, salvo quando a lei permitir ou exigir a guarda.',
    ],
  },
  {
    index: '08',
    title: 'Segurança',
    paragraphs: [
      'O site usa conexão criptografada (HTTPS), e adotamos medidas técnicas e administrativas para proteger os dados.',
      'Nenhum sistema é totalmente imune a incidentes. Se ocorrer um incidente relevante, faremos as comunicações exigidas pela LGPD.',
    ],
  },
  {
    index: '09',
    title: 'Seus direitos',
    paragraphs: ['A LGPD garante a você, entre outros, os direitos abaixo (art. 18):'],
    items: [
      { text: 'Confirmar se tratamos os seus dados e ter acesso a eles.' },
      { text: 'Corrigir dados incompletos, inexatos ou desatualizados.' },
      {
        text: 'Pedir a anonimização, o bloqueio ou a eliminação de dados desnecessários, excessivos ou tratados em desacordo com a lei.',
      },
      {
        text: 'Pedir a portabilidade dos dados a outro fornecedor, conforme a regulamentação da ANPD.',
      },
      { text: 'Saber com quem compartilhamos os seus dados.' },
      {
        text: 'Revogar o consentimento, quando ele for a base do tratamento, e se opor a tratamentos irregulares.',
      },
      { text: 'Apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).' },
    ],
  },
  {
    index: '10',
    title: 'Como falar com a gente',
    paragraphs: [
      'Para exercer os seus direitos ou tirar dúvidas sobre esta política, use o nosso canal de atendimento ao titular de dados. Podemos pedir informações para confirmar a sua identidade antes de atender ao pedido.',
    ],
    showContactEmail: true,
  },
  {
    index: '11',
    title: 'Alterações nesta política',
    paragraphs: [
      'Podemos atualizar esta política quando o site, os fornecedores ou a legislação mudarem. A data da última atualização aparece no topo desta página.',
    ],
  },
]
